import { zodResolver } from '@hookform/resolvers/zod';
import type { UseNavigateResult } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMe } from '@/api/me';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { UpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateDetails } from '@/features/estates-property/api/estate-details';
import { useProject } from '@/features/projects/api/projects';
import { Modal } from './new/modal';
import type { useModal } from './new/use-modal';

const VALID_ESTATE_PROPERTY_TYPES = ['house', 'apartment', 'industry', 'room', 'other'] as const;

export interface CreateProjectObjectModalProps {
  project: {
    id: string;
    name: string;
  };
  navigate: UseNavigateResult<string>;
  onSuccess?: (estateId: string) => void;
  modal: ReturnType<typeof useModal>;
}

export const CreateProjectObjectModal: React.FC<CreateProjectObjectModalProps> = ({
  project,
  navigate,
  modal,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isCustomAddressOpen, setIsCustomAddressOpen] = useState(false);
  const schema = getCreateEstateSchema({ isAddressRequired: isCustomAddressOpen });
  const { data: me } = useMe();
  const { data: projectData } = useProject({ id: project.id });

  const defaultValues: EstateSchemaType = {
    estateData: {
      name: '',
      propertyType: '',
      marketType: '',
      availability: null,
      tenantId: projectData.tenant?.id ?? '',
      projectId: project.id,
      creatorId: me?.id,
      propertyLevel: PROPERTY_LEVELS.OBJECT,
    },
    addressData: {
      address: '',
      postalCode: '',
      city: '',
      countryId: '',
      street: '',
      houseNumber: '',
    },
  };

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { mutateAsync: updateEstateDetails } = useUpdateEstateDetails({});

  const { mutateAsync: updateEstateAreas } = useUpdateEstateAreas({});

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: async ([createdEstate]) => {
        if (projectData) {
          const promises: Promise<unknown>[] = [];
          if (projectData.details) {
            // Keep the types chosen in the form: an empty value here overwrote what create saved
            const { marketType, propertyType } = methods.getValues('estateData');
            promises.push(
              updateEstateDetails({
                id: createdEstate.id,
                constructionType: '',
                condition: '',
                buildingYear: projectData.details.buildingYear || '',
                heatingType: [],
                heatGeneration: '',
                marketType: marketType || '',
                usageType: projectData.details.usageType || '',
                typeOfGlass: '',
                flooring: '',
                propertyType: propertyType || '',
              })
            );
          }
          if (projectData.areaRooms) {
            const updatedAreas: UpdateEstateAreas = {
              id: createdEstate.id,
              totalNumberOfRooms: 0,
            };
            if (
              projectData?.areaRooms.hasElevator !== null &&
              projectData.areaRooms.hasElevator !== undefined
            ) {
              updatedAreas.hasElevator = projectData.areaRooms.hasElevator;
            }
            if ('hasElevator' in updatedAreas) {
              promises.push(updateEstateAreas(updatedAreas));
            }
          }
          await Promise.all(promises);
        }
        if (onSuccess) {
          onSuccess(createdEstate.id);
        }
        navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        methods.reset(defaultValues);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  useEffect(() => {
    if (projectData) {
      const projectPropertyType = projectData.details?.propertyType;
      const isValidProjectPropertyType =
        typeof projectPropertyType === 'string' &&
        VALID_ESTATE_PROPERTY_TYPES.includes(
          projectPropertyType as (typeof VALID_ESTATE_PROPERTY_TYPES)[number]
        );

      methods.reset((prev) => ({
        ...prev,
        estateData: {
          ...prev.estateData,
          tenantId: projectData.tenant?.id ?? '',
          projectId: project.id,
          ...(projectData?.details?.marketType && { marketType: projectData.details.marketType }),
          ...(isValidProjectPropertyType && {
            propertyType: projectPropertyType,
          }),
          ...(projectData?.details?.usageType && { usageType: projectData.details.usageType }),
          ...(projectData?.details?.buildingYear && {
            buildingYear: projectData.details.buildingYear,
          }),
        },
        addressData: {
          ...prev.addressData,
          ...(projectData?.address?.address && {
            addressAutoComplete: projectData.address.address,
          }),
          ...(projectData?.address?.city && { city: projectData.address.city }),
          ...(projectData?.address?.country?.id && { countryId: projectData.address.country.id }),
          ...(projectData?.address?.country?.code && {
            countryCode: projectData.address.country.code,
          }),
          ...(projectData?.address?.postalCode && { postalCode: projectData.address.postalCode }),
          street: projectData?.address?.street || '',
          houseNumber: projectData?.address?.houseNumber || '',
        },
      }));
      // To trigger the watch()
      if (projectData?.address?.lat && projectData?.address?.long) {
        methods.setValue('addressData.lat', projectData.address.lat);
        methods.setValue('addressData.long', projectData.address.long);
      }
    }
  }, [methods, projectData, project.id]);

  const onSubmit = async (args: EstateSchemaType) => {
    await createEstateAsync({
      ...args,
      estateData: {
        ...args.estateData,
        projectId: project.id,
        ...(projectData?.energyDetails?.id && {
          estateEnergyDetailsId: projectData.energyDetails.id,
        }),
      },
      addressData: isCustomAddressOpen
        ? {
            ...args.addressData,
          }
        : {
            address: '',
            postalCode: '',
            city: '',
            countryId: '',
            street: '',
            houseNumber: '',
          },
    });
  };

  return (
    <Modal {...modal} title="" className="w-max max-w-[66vw]">
      <FormProvider {...methods}>
        <EstateForm
          closeModal={modal.dismiss}
          label={t('add-new-estate')}
          onSubmit={onSubmit}
          options={{
            isAddressOptional: true,
            isAddressOpen: isCustomAddressOpen,
            onAddressOpenChange: setIsCustomAddressOpen,
            projectDisabled: true,
            propertyLevelDisabled: true,
            tenantDisabled: true,
          }}
        />
      </FormProvider>
    </Modal>
  );
};
